import { useEffect, useState } from "react";
import { AppLayout } from "@/components/AppLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { UserCircle2, CheckCircle2, GraduationCap, Briefcase } from "lucide-react";
import { parseCURP, edadEnFecha, isValidCURP } from "@/lib/curp";

const ESTADOS_CIVIL = ["SOLTERO(A)", "CASADO(A)", "UNIÓN LIBRE", "DIVORCIADO(A)", "VIUDO(A)"];
const DISC_OPTS = ["", "CEGUERA", "BAJA VISIÓN", "SORDERA", "HIPOACUSIA", "MOTRIZ", "INTELECTUAL", "PSICOSOCIAL", "OTRA"];
const NIV_ESTUDIOS = [
  "Bachillerato terminado",
  "Profesional técnico",
  "Normal preescolar terminada",
  "Normal primaria terminada",
  "Normal superior pasante",
  "Normal superior titulado",
  "Licenciatura pasante",
  "Licenciatura titulado",
  "Maestría incompleta",
  "Maestría graduado",
  "Doctorado incompleto",
  "Doctorado graduado",
  "Otro",
];
const FUNCIONES = [
  "Docente frente a grupo", "Directivo con grupo", "Directivo sin grupo", "Subdirector académico",
  "Subdirector gestión", "Profesor educ. física", "Profesor artísticas", "Profesor idiomas",
  "Administrativo", "Otra",
];

// SEP 911 grados de estudios (formato Director)
const GRADO_ESTUDIOS_TIPOS = [
  ["NORMAL_PREESCOLAR", "Normal preescolar", ["INCOMPLETA", "TERMINADA"]],
  ["NORMAL_PRIMARIA", "Normal primaria", ["INCOMPLETA", "TERMINADA"]],
  ["NORMAL_SUPERIOR", "Normal superior", ["INCOMPLETA", "PASANTE", "TITULADO"]],
  ["LICENCIATURA", "Licenciatura", ["INCOMPLETA", "PASANTE", "TITULADO"]],
  ["MAESTRIA", "Maestría", ["INCOMPLETA", "GRADUADO"]],
  ["DOCTORADO", "Doctorado", ["INCOMPLETA", "GRADUADO"]],
  ["OTROS", "Otros", []],
];

const CARGO_DIRECTOR = [
  ["DIRECTOR_CON_GRUPO", "Director con grupo"],
  ["DIRECTOR_SIN_GRUPO", "Director sin grupo"],
];

const emptyProfile = {
  nombre: "", apellido_paterno: "", apellido_materno: "",
  curp: "", rfc: "", telefono: "",
  fecha_nacimiento: "", genero: "", estado_civil: "", plaza: "",
  anios_servicio: 0, discapacidad: "", lenguas_indigenas: "",
  nivel_estudios: "", funcion: "",
  // Director-specific
  fecha_ingreso_sep: "", cargo: "",
  grado_estudios_tipo: "", grado_estudios_estatus: "", otros_estudios: "",
};

export default function MiPerfil() {
  const { user, refreshUser, fechaCorteEdad } = useAuth();
  const [form, setForm] = useState(emptyProfile);
  const [saving, setSaving] = useState(false);
  const [nivelPlantel, setNivelPlantel] = useState("");
  // Flag: true si el usuario ha editado el RFC manualmente. Cuando es false, la CURP puede reescribir el RFC.
  const [rfcManual, setRfcManual] = useState(false);
  const isDirector = user?.role === "director";
  const currentTipo = GRADO_ESTUDIOS_TIPOS.find(([k]) => k === form.grado_estudios_tipo);
  const estatusOpts = currentTipo?.[2] || [];
  const cutoff = fechaCorteEdad?.(nivelPlantel) || "";
  const edadCumplida = form.fecha_nacimiento && cutoff ? edadEnFecha(form.fecha_nacimiento, cutoff) : "";

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        setForm({ ...emptyProfile, ...data });
        // Al cargar el perfil, si el RFC guardado NO coincide con el prefijo de la CURP,
        // asumimos que fue capturado manualmente y bloqueamos el auto-fill.
        const rfc0 = (data.rfc || "").toUpperCase();
        const curp0 = (data.curp || "").toUpperCase();
        setRfcManual(!!rfc0 && (curp0.length < 10 || rfc0 !== curp0.slice(0, 10)));
        const pid = (data.plantel_ids || [])[0];
        if (pid) {
          try {
            const { data: plantel } = await api.get(`/planteles/${pid}`);
            setNivelPlantel(plantel?.nivel || "");
          } catch { /* ignore */ }
        }
      } catch (e) { toast.error(formatApiError(e)); }
    })();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (form.curp && !isValidCURP(form.curp)) {
      toast.error("La CURP debe tener 18 caracteres, con dígitos en las posiciones 5 a 10.");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, perfil_completo: true };
      await api.put(`/usuarios/${user.id}`, payload);
      toast.success("Perfil guardado");
      await refreshUser?.();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  return (
    <AppLayout>
      <div className="max-w-4xl">
        <div className="flex items-start justify-between mb-6">
          <div>
            <div className="text-xs tracking-[0.2em] uppercase text-[#9F2241] mb-1">Cuenta</div>
            <h1 className="text-3xl font-semibold text-[#10312B] tracking-tight" style={{ fontFamily: "'Montserrat', sans-serif" }}>
              Mi perfil
            </h1>
            <p className="text-sm text-[#545454] mt-1">
              Complete sus datos personales requeridos por el formato 911.
            </p>
          </div>
          {form.perfil_completo && (
            <div className="flex items-center gap-1.5 text-[#235B4E] text-sm">
              <CheckCircle2 className="w-4 h-4" /> Perfil completo
            </div>
          )}
        </div>

        <Card className="border-[#E5E7EB]">
          <CardContent className="p-6">
            <form onSubmit={submit} className="space-y-6">
              <Section title="Datos personales" icon={UserCircle2}>
                <div className="grid md:grid-cols-3 gap-3">
                  <Field label="Nombre(s)" required>
                    <Input value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value.toUpperCase() })} data-testid="perfil-nombre" required />
                  </Field>
                  <Field label="Apellido paterno">
                    <Input value={form.apellido_paterno} onChange={(e) => setForm({ ...form, apellido_paterno: e.target.value.toUpperCase() })} />
                  </Field>
                  <Field label="Apellido materno">
                    <Input value={form.apellido_materno} onChange={(e) => setForm({ ...form, apellido_materno: e.target.value.toUpperCase() })} />
                  </Field>
                  <Field label="CURP">
                    <Input
                      value={form.curp}
                      onChange={(e) => {
                        const curp = e.target.value.toUpperCase();
                        const parsed = parseCURP(curp);
                        const rfcAutoPrefix = curp.length >= 10 ? curp.slice(0, 10) : "";
                        setForm((f) => ({
                          ...f,
                          curp,
                          fecha_nacimiento: parsed?.fecha_nacimiento || f.fecha_nacimiento,
                          genero: parsed?.genero || f.genero,
                          // Solo sobreescribimos el RFC si el usuario NO lo editó a mano
                          rfc: !rfcManual && rfcAutoPrefix ? rfcAutoPrefix : f.rfc,
                        }));
                      }}
                      maxLength={18}
                      className="font-mono"
                      data-testid="perfil-curp"
                    />
                    {form.curp && form.curp.length > 0 && !isValidCURP(form.curp) && (
                      <p className="text-[11px] text-[#9F2241] mt-1" data-testid="perfil-curp-error">
                        Formato de CURP inválido. Debe tener 18 caracteres (4 letras + 6 dígitos + H/M + 5 letras + 2 alfanuméricos).
                      </p>
                    )}
                  </Field>
                  <Field label="RFC" hint={!rfcManual && form.curp?.length >= 10 ? "Prefijo autocompletado desde la CURP — agregue homoclave (3 caracteres)" : ""}>
                    <Input
                      value={form.rfc}
                      onChange={(e) => {
                        const val = e.target.value.toUpperCase();
                        // Si el usuario borra todo el RFC, permitimos que la CURP lo vuelva a autollenar.
                        setRfcManual(val.length > 0);
                        setForm({ ...form, rfc: val });
                      }}
                      maxLength={13}
                      className="font-mono"
                      data-testid="perfil-rfc"
                    />
                  </Field>
                  <Field label="Género" hint={parseCURP(form.curp)?.genero ? "Autocompletado desde la CURP" : ""}>
                    <Select
                      key={`gen-${form.genero || "none"}`}
                      value={form.genero || "__none__"}
                      onValueChange={(v) => setForm({ ...form, genero: v === "__none__" ? "" : v })}
                      disabled={!!parseCURP(form.curp)?.genero}
                    >
                      <SelectTrigger data-testid="perfil-genero" className={parseCURP(form.curp)?.genero ? "bg-[#F3F4F6]" : ""}><SelectValue placeholder="—" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">— Sin especificar —</SelectItem>
                        <SelectItem value="H">Hombre</SelectItem>
                        <SelectItem value="M">Mujer</SelectItem>
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label="Fecha de nacimiento" hint={parseCURP(form.curp)?.fecha_nacimiento ? "Autocompletado desde la CURP" : ""}>
                    <Input
                      type="date"
                      data-testid="perfil-fecha-nac"
                      value={form.fecha_nacimiento || ""}
                      onChange={(e) => setForm({ ...form, fecha_nacimiento: e.target.value })}
                      readOnly={!!parseCURP(form.curp)?.fecha_nacimiento}
                      className={parseCURP(form.curp)?.fecha_nacimiento ? "bg-[#F3F4F6] cursor-not-allowed" : ""}
                    />
                  </Field>
                  <Field label={cutoff ? `Edad cumplida al ${cutoff}` : "Edad cumplida"} hint="Se calcula con la fecha de corte configurada por Superadmin">
                    <Input
                      data-testid="perfil-edad"
                      value={edadCumplida !== "" ? `${edadCumplida} años` : ""}
                      readOnly
                      className="bg-[#F3F4F6] cursor-not-allowed font-semibold"
                    />
                  </Field>
                  <Field label="Estado civil">
                    <Select value={form.estado_civil || "__none__"} onValueChange={(v) => setForm({ ...form, estado_civil: v === "__none__" ? "" : v })}>
                      <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">— Sin especificar —</SelectItem>
                        {ESTADOS_CIVIL.map((e) => <SelectItem key={e} value={e}>{e}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </Field>
                  {isDirector && (
                    <Field label="Teléfono oficial">
                      <Input value={form.telefono} onChange={(e) => setForm({ ...form, telefono: e.target.value })} data-testid="perfil-telefono" />
                    </Field>
                  )}
                  <Field label="Celular personal">
                    <Input value={form.celular || ""} onChange={(e) => setForm({ ...form, celular: e.target.value })} data-testid="perfil-celular" />
                  </Field>
                  {isDirector && (
                    <Field label="Correo institucional" hint="El correo con el que se firma el reporte 911">
                      <Input type="email" value={form.correo_institucional || ""} onChange={(e) => setForm({ ...form, correo_institucional: e.target.value })} data-testid="perfil-correo-inst" placeholder={form.email || ""} />
                    </Field>
                  )}
                  <Field label="Correo personal">
                    <Input type="email" value={form.correo_personal || ""} onChange={(e) => setForm({ ...form, correo_personal: e.target.value })} data-testid="perfil-correo-personal" />
                  </Field>
                </div>
              </Section>

              {isDirector ? (
                <>
                  <Section title="Cargo y fecha de ingreso al servicio" icon={Briefcase}>
                    <div className="grid md:grid-cols-3 gap-3">
                      <Field label="Fecha de ingreso al servicio" required>
                        <Input
                          type="date"
                          value={form.fecha_ingreso_sep || ""}
                          onChange={(e) => setForm({ ...form, fecha_ingreso_sep: e.target.value })}
                          data-testid="perfil-fecha-ingreso-sep"
                        />
                      </Field>
                      <Field label={`Antigüedad al 1° sept ${new Date().getFullYear()}`} hint="Calculada desde la fecha de ingreso al servicio">
                        <Input
                          data-testid="perfil-antiguedad"
                          value={(() => {
                            if (!form.fecha_ingreso_sep) return "";
                            const ingreso = new Date(form.fecha_ingreso_sep);
                            if (isNaN(ingreso.getTime())) return "";
                            const y = new Date().getFullYear();
                            const corte = new Date(y, 8, 1); // 1 sept (mes 8, 0-indexed)
                            let years = corte.getFullYear() - ingreso.getFullYear();
                            let months = corte.getMonth() - ingreso.getMonth();
                            let days = corte.getDate() - ingreso.getDate();
                            if (days < 0) { months--; }
                            if (months < 0) { years--; months += 12; }
                            if (years < 0) return "—";
                            return `${years} año${years === 1 ? "" : "s"}${months ? ` · ${months} mes${months === 1 ? "" : "es"}` : ""}`;
                          })()}
                          readOnly
                          className="bg-[#F3F4F6] cursor-not-allowed font-semibold"
                        />
                      </Field>
                      <Field label="Cargo" required>
                        <Select
                          value={form.cargo || "__none__"}
                          onValueChange={(v) => setForm({ ...form, cargo: v === "__none__" ? "" : v })}
                        >
                          <SelectTrigger data-testid="perfil-cargo"><SelectValue placeholder="—" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__none__">— Sin especificar —</SelectItem>
                            {CARGO_DIRECTOR.map(([k, label]) => (
                              <SelectItem key={k} value={k}>{label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </Field>
                      <Field label="Plaza / Clave">
                      <Input value={form.plaza} onChange={(e) => setForm({ ...form, plaza: e.target.value.toUpperCase() })} placeholder="Ej. E0281" />
                      </Field>
                    </div>
                  </Section>

                  <Section title="Grado máximo de estudios" icon={GraduationCap}>
                    <div className="grid md:grid-cols-3 gap-3">
                      <Field label="Tipo" required>
                        <Select
                          value={form.grado_estudios_tipo || "__none__"}
                          onValueChange={(v) => setForm({
                            ...form,
                            grado_estudios_tipo: v === "__none__" ? "" : v,
                            grado_estudios_estatus: "",
                          })}
                        >
                          <SelectTrigger data-testid="perfil-grado-tipo"><SelectValue placeholder="—" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__none__">— Sin especificar —</SelectItem>
                            {GRADO_ESTUDIOS_TIPOS.map(([k, label]) => (
                              <SelectItem key={k} value={k}>{label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </Field>
                      {estatusOpts.length > 0 && (
                        <Field label="Estatus" required>
                          <Select
                            value={form.grado_estudios_estatus || "__none__"}
                            onValueChange={(v) => setForm({ ...form, grado_estudios_estatus: v === "__none__" ? "" : v })}
                          >
                            <SelectTrigger data-testid="perfil-grado-estatus"><SelectValue placeholder="—" /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="__none__">— Sin especificar —</SelectItem>
                              {estatusOpts.map((s) => (
                                <SelectItem key={s} value={s}>{s}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </Field>
                      )}
                      {form.grado_estudios_tipo === "OTROS" && (
                        <Field label="Especifique">
                          <Input
                            data-testid="perfil-otros-estudios"
                            value={form.otros_estudios || ""}
                            onChange={(e) => setForm({ ...form, otros_estudios: e.target.value.toUpperCase() })}
                            placeholder="Detalle del grado de estudios"
                          />
                        </Field>
                      )}
                    </div>
                  </Section>
                </>
              ) : (
                <Section title="Datos laborales">
                  <div className="grid md:grid-cols-3 gap-3">
                    <Field label="Plaza / Clave">
                      <Input value={form.plaza} onChange={(e) => setForm({ ...form, plaza: e.target.value.toUpperCase() })} placeholder="Ej. E0281" />
                    </Field>
                    <Field label="Función">
                      <Select value={form.funcion || "__none__"} onValueChange={(v) => setForm({ ...form, funcion: v === "__none__" ? "" : v })}>
                        <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">— Sin especificar —</SelectItem>
                          {FUNCIONES.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </Field>
                    <Field label="Fecha de ingreso al servicio">
                      <Input
                        type="date"
                        value={form.fecha_ingreso_sep || ""}
                        onChange={(e) => setForm({ ...form, fecha_ingreso_sep: e.target.value })}
                        data-testid="perfil-fecha-ingreso-sep-docente"
                      />
                    </Field>
                    <Field label={`Antigüedad al 1° sept ${new Date().getFullYear()}`} hint="Calculada desde la fecha de ingreso al servicio">
                      <Input
                        data-testid="perfil-antiguedad-docente"
                        value={(() => {
                          if (!form.fecha_ingreso_sep) return "";
                          const ingreso = new Date(form.fecha_ingreso_sep);
                          if (isNaN(ingreso.getTime())) return "";
                          const y = new Date().getFullYear();
                          const corte = new Date(y, 8, 1);
                          let years = corte.getFullYear() - ingreso.getFullYear();
                          let months = corte.getMonth() - ingreso.getMonth();
                          let days = corte.getDate() - ingreso.getDate();
                          if (days < 0) { months--; }
                          if (months < 0) { years--; months += 12; }
                          if (years < 0) return "—";
                          return `${years} año${years === 1 ? "" : "s"}${months ? ` · ${months} mes${months === 1 ? "" : "es"}` : ""}`;
                        })()}
                        readOnly
                        className="bg-[#F3F4F6] cursor-not-allowed font-semibold"
                      />
                    </Field>
                    <Field label="Nivel de estudios">
                      <Select value={form.nivel_estudios || "__none__"} onValueChange={(v) => setForm({ ...form, nivel_estudios: v === "__none__" ? "" : v })}>
                        <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">— Sin especificar —</SelectItem>
                          {NIV_ESTUDIOS.map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </Field>
                  </div>
                </Section>
              )}

              {!["supervisor_zona", "jefe_sector", "director"].includes(user?.role) && (
              <Section title="Condiciones y lengua">
                <div className="grid md:grid-cols-2 gap-3">
                  <Field label="Discapacidad">
                    <Select value={form.discapacidad || "__none__"} onValueChange={(v) => setForm({ ...form, discapacidad: v === "__none__" ? "" : v })}>
                      <SelectTrigger><SelectValue placeholder="Ninguna" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">Ninguna</SelectItem>
                        {DISC_OPTS.filter(Boolean).map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label="Lenguas indígenas que habla" hint="Separadas por coma (ej. NAH:Náhuatl, MAY:Maya)">
                    <Input value={form.lenguas_indigenas} onChange={(e) => setForm({ ...form, lenguas_indigenas: e.target.value.toUpperCase() })} />
                  </Field>
                </div>
              </Section>
              )}

              <div className="flex justify-end">
                <Button data-testid="btn-save-perfil" type="submit" disabled={saving} className="bg-[#235B4E] hover:bg-[#1D4A40]">
                  {saving ? "Guardando…" : "Guardar perfil"}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  );
}

function Section({ title, icon: Icon, children }) {
  return (
    <section>
      <div className="flex items-center gap-2 mb-3 pb-1 border-b border-[#E5E7EB]">
        {Icon && <Icon className="w-4 h-4 text-[#235B4E]" />}
        <h2 className="text-sm font-semibold uppercase tracking-wider text-[#235B4E]">{title}</h2>
      </div>
      {children}
    </section>
  );
}

function Field({ label, children, required, hint }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs">{label}{required && <span className="text-[#9F2241] ml-1">*</span>}</Label>
      {children}
      {hint && <p className="text-[10px] text-[#9CA3AF]">{hint}</p>}
    </div>
  );
}
